import { RootLayout } from '@/app/layouts/RootLayout'
import { AccessDeniedPage } from '@/app/pages/AccessDeniedPage'
import { LoginPage } from '@/app/pages/LoginPage'
import { NotFoundPage } from '@/app/pages/NotFoundPage'
import { WelcomePage } from '@/app/routes/lazyPages'
import { authenticatedRouteGuard, redirectToFirstOrg } from '@/lib/routeGuards'
import { accessDeniedSearchSchema } from '@/lib/searchSchemas'
import { useAuthStore } from '@/stores/authStore'
import { isConsoleDeployment, isPlatformDeployment } from '@/utils/deployment'
import { createRootRoute, createRoute, redirect } from '@tanstack/react-router'
import { z } from 'zod'

/**
 * Routes that sit above tenancy: the shell, the entry redirect, login, onboarding, and the two pages a
 * URL falls through to.
 */
export const rootRoute = createRootRoute({
	validateSearch: z
		.object({
			filters: z.string().optional(),
		})
		.passthrough(),
	component: RootLayout,
	// Adding authentication context to the root route
	beforeLoad: () => {
		return {
			isAuthenticated: useAuthStore.getState().isAuthenticated,
		}
	},
})

// Create an index route that redirects to the project route based on permissions
export const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/',
	beforeLoad: ({ context, location }) => {
		authenticatedRouteGuard(context, { location })

		const authState = useAuthStore.getState()
		const isAuthenticated = authState.isAuthenticated

		if (isAuthenticated) {
			if (isConsoleDeployment()) {
				const user = authState.user
				if (user?.organizations?.length && user.organizations.length > 0) {
					throw redirectToFirstOrg()
				}
				throw redirect({ to: '/welcome' })
			}
			// Platform deployment
			throw redirectToFirstOrg()
		}
		throw redirect({ to: '/login' })
	},
})

// Login route
export const loginRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/login',
	component: () => <LoginPage />,
	beforeLoad: ({ context, location }) => {
		// Redirect to the home page if the user is already authenticated
		if (context.isAuthenticated) {
			// Check if there's a redirect parameter from auth guard
			const redirectTo = (location.search as { redirect?: string })?.redirect
			if (redirectTo && redirectTo !== '/login') {
				throw redirect({
					to: redirectTo,
					replace: true,
				})
			}
			throw redirect({
				to: '/',
				replace: true,
			})
		}
	},
})

// Welcome route — plan selection / onboarding when user has no org or tenant access
export const welcomeRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/welcome',
	component: () => <WelcomePage />,
	beforeLoad: ({ context, location }) => {
		authenticatedRouteGuard(context, { location })
		if (isPlatformDeployment()) {
			throw redirectToFirstOrg()
		}
	},
})

export const notFoundRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '*',
	component: () => <NotFoundPage />,
})

export const globalAccessDeniedRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/access-denied',
	validateSearch: accessDeniedSearchSchema,
	component: () => <AccessDeniedPage />,
})
